import { Controller } from "@hotwired/stimulus";

type RequestEvent = CustomEvent<{ elt?: Element; successful?: boolean; xhr?: XMLHttpRequest }>;
type Timing = { mode: string; amount: string; unit: string; date: string };

export default class extends Controller<HTMLFormElement> {
    static targets = ["amount", "unit", "amountLabel", "unitLabel", "date", "calendar", "scheduledAt", "label", "controls", "overdue", "error"];
    declare readonly amountTarget: HTMLInputElement;
    declare readonly unitTarget: HTMLSelectElement;
    declare readonly amountLabelTarget: HTMLButtonElement;
    declare readonly unitLabelTarget: HTMLButtonElement;
    declare readonly dateTarget: HTMLInputElement;
    declare readonly calendarTarget: HTMLElement & { value: string; focusedDate: string };
    declare readonly scheduledAtTarget: HTMLInputElement;
    declare readonly labelTarget: HTMLButtonElement;
    declare readonly controlsTarget: HTMLElement;
    declare readonly overdueTarget: HTMLElement;
    declare readonly errorTarget: HTMLElement;
    private busy = false;
    private pending = false;
    private saved = "";
    private submitted = "";
    private timer?: ReturnType<typeof setInterval>;

    async connect(): Promise<void> {
        const moduleUrl = "/static/calendar-elements.js";
        await import(moduleUrl);
        if (!this.element.isConnected) return;
        this.readServerValues();
        this.timer = setInterval(() => this.syncWarning(), 60000);
        document.addEventListener("elron:after-morphdom", this.refreshWarning);
    }

    disconnect(): void {
        clearInterval(this.timer);
        document.removeEventListener("elron:after-morphdom", this.refreshWarning);
        window.removeEventListener("resize", this.position);
        document.removeEventListener("scroll", this.position, true);
    }

    editAmount(event: Event): void { this.editRelative(event, this.amountTarget, this.amountLabelTarget); }
    editUnit(event: Event): void { this.editRelative(event, this.unitTarget, this.unitLabelTarget); }

    private editRelative(event: Event, input: HTMLInputElement | HTMLSelectElement, label: HTMLElement): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.busy && this.closed) this.readServerValues();
        this.element.setAttribute("data-stream-preserve-self", "true");
        input.classList.remove("hidden");
        label.classList.add("hidden");
        input.focus({ preventScroll: true });
        if (input instanceof HTMLInputElement) input.select();
    }

    edit(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (this.controlsTarget.matches(":popover-open")) { this.leave(); return; }
        if (!this.busy && this.closed) this.readServerValues();
        this.element.setAttribute("data-stream-preserve-self", "true");
        // Closed editors can be morphed, removing client-only attributes.
        this.controlsTarget.setAttribute("popover", "manual");
        this.controlsTarget.classList.remove("hidden");
        this.controlsTarget.showPopover();
        this.labelTarget.setAttribute("aria-expanded", "true");
        this.position();
        window.addEventListener("resize", this.position);
        document.addEventListener("scroll", this.position, true);
        this.dateTarget.focus({ preventScroll: true });
    }

    private position = (): void => {
        if (!this.fixed || !this.controlsTarget.matches(":popover-open")) return;
        const anchor = this.labelTarget.getBoundingClientRect();
        const panel = this.controlsTarget.getBoundingClientRect();
        const gap = 8;
        const left = Math.max(gap, Math.min(anchor.left, window.innerWidth - panel.width - gap));
        const top = anchor.bottom + gap + panel.height <= window.innerHeight - gap
            ? anchor.bottom + gap
            : Math.max(gap, anchor.top - panel.height - gap);
        Object.assign(this.controlsTarget.style, { left: `${left}px`, top: `${top}px` });
    };

    private readServerValues(): void {
        if (this.fixed) {
            this.calendarTarget.setAttribute("locale", this.french ? "fr-FR" : "en-GB");
            this.scheduledAtTarget.value = this.element.dataset.waitScheduledAt ?? "";
            this.dateTarget.value = this.scheduledAtTarget.value ? this.localDate(new Date(this.scheduledAtTarget.value)) : "";
            this.dateTarget.setCustomValidity("");
            this.dateTarget.required = true;
            this.syncCalendar();
        } else {
            this.amountTarget.value = this.element.dataset.waitAmount ?? "1";
            this.unitTarget.value = this.element.dataset.waitUnit ?? "days";
        }
        this.syncBounds();
        this.saved = this.value();
    }

    selectDate(event: Event): void {
        event.stopPropagation();
        const time = this.dateTarget.value.split("T")[1] || "09:00";
        this.dateTarget.value = `${this.calendarTarget.value}T${time}`;
        this.save();
    }

    private syncCalendar(): void {
        if (!this.fixed) return;
        const date = this.dateTarget.value.slice(0, 10);
        this.calendarTarget.value = date;
        this.calendarTarget.focusedDate = date || this.localDate(new Date()).slice(0, 10);
    }

    private syncBounds(): void {
        if (!this.fixed) {
            const divisor = this.unitTarget.value === "days" ? 86400 : this.unitTarget.value === "hours" ? 3600 : this.unitTarget.value === "minutes" ? 60 : 1;
            this.amountTarget.min = "1";
            this.amountTarget.step = "1";
            this.amountTarget.max = String(365 * 86400 / divisor);
        }
        this.syncWarning();
        this.position();
    }

    private readonly refreshWarning = (): void => { if (this.element.isConnected) this.syncWarning(); };

    private syncWarning(): void {
        if (!this.fixed) return;
        const closed = this.closed;
        const instant = new Date(closed ? this.element.dataset.waitScheduledAt ?? "" : this.dateTarget.value);
        this.overdueTarget.classList.toggle("hidden", !Number.isFinite(instant.getTime()) || instant.getTime() > Date.now());
    }

    save(): void {
        this.syncCalendar();
        this.syncBounds();
        if (this.fixed) this.dateTarget.setCustomValidity("");
        if (this.fixed && this.dateTarget.value) {
            const date = new Date(this.dateTarget.value);
            if (!Number.isFinite(date.getTime()) || this.localDate(date) !== this.withSeconds(this.dateTarget.value)) {
                this.dateTarget.setCustomValidity(this.french ? "Cette heure n’existe pas dans votre fuseau horaire." : "This time does not exist in your timezone.");
            } else this.scheduledAtTarget.value = date.toISOString();
        }
        if (!this.element.reportValidity()) return;
        if (this.busy) { this.pending = true; return; }
        if (this.value() === this.saved) return;
        this.element.dispatchEvent(new CustomEvent("wait-save", { bubbles: true }));
    }

    submit(event: Event): void { event.preventDefault(); this.save(); }

    keydown(event: KeyboardEvent): void {
        if (event.key === "Enter" && !event.isComposing && (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) {
            event.preventDefault();
            event.stopPropagation();
            this.leave();
            if (this.element.checkValidity()) event.target.blur();
        }
        if (event.key === "Escape") {
            event.preventDefault();
            if (!this.busy) {
                const saved = JSON.parse(this.saved) as Timing;
                if (this.fixed) {
                    this.dateTarget.value = saved.date;
                    this.syncCalendar();
                    this.dateTarget.setCustomValidity("");
                } else {
                    this.amountTarget.value = saved.amount;
                    this.unitTarget.value = saved.unit;
                }
                this.syncBounds();
                this.close();
                (this.fixed ? this.labelTarget : this.amountLabelTarget).focus({ preventScroll: true });
            }
        }
    }

    outside(event: Event): void {
        if (event.target instanceof Node && !this.element.contains(event.target)) this.leave();
    }

    blur(event: FocusEvent): void {
        if (event.relatedTarget instanceof Node && this.element.contains(event.relatedTarget)) return;
        queueMicrotask(() => { if (!this.busy && !this.element.contains(document.activeElement)) this.leave(); });
    }

    private leave(): void {
        if (this.closed) return;
        this.save();
        if (this.element.checkValidity()) this.close();
    }

    private close(): void {
        if (this.fixed) {
            this.controlsTarget.hidePopover();
            this.controlsTarget.classList.add("hidden");
            this.labelTarget.setAttribute("aria-expanded", "false");
            window.removeEventListener("resize", this.position);
            document.removeEventListener("scroll", this.position, true);
        } else {
            this.amountTarget.classList.add("hidden");
            this.unitTarget.classList.add("hidden");
            this.amountLabelTarget.classList.remove("hidden");
            this.unitLabelTarget.classList.remove("hidden");
        }
        if (!this.busy) this.element.removeAttribute("data-stream-preserve-self");
    }

    beforeRequest(event: RequestEvent): void {
        if (event.detail.elt?.hasAttribute("data-wait-mode-switch")) {
            this.errorTarget.classList.add("hidden");
            return;
        }
        if (event.detail.elt !== this.element) return;
        this.busy = true;
        this.element.setAttribute("data-stream-preserve-self", "true");
        this.submitted = this.value();
        this.errorTarget.classList.add("hidden");
    }

    afterRequest(event: RequestEvent): void {
        if (event.detail.elt?.hasAttribute("data-wait-mode-switch")) {
            this.errorTarget.classList.toggle("hidden", !!event.detail.successful);
            // The server renders the new editor through the normal detail refresh.
            return;
        }
        if (event.detail.elt !== this.element) return;
        this.busy = false;
        if (event.detail.successful) {
            this.saved = this.submitted;
            const saved = JSON.parse(this.saved) as Timing;
            Object.assign(this.element.dataset, {
                waitMode: saved.mode, waitAmount: saved.amount, waitUnit: saved.unit,
                waitScheduledAt: saved.mode === "fixed" ? new Date(saved.date).toISOString() : "",
            });
            this.syncWarning();
            const prefix = this.element.dataset.waitPrefix ?? "";
            if (saved.mode === "fixed") {
                this.labelTarget.textContent = prefix + (this.element.dataset.waitFixedLabel ?? (this.french ? "Réveiller le " : "Wake up on ")) + new Intl.DateTimeFormat(this.french ? "fr-FR" : "en-GB", { dateStyle: "short", timeStyle: "short" }).format(new Date(saved.date));
            } else {
                const seconds = Number(saved.amount) * (saved.unit === "days" ? 86400 : saved.unit === "hours" ? 3600 : saved.unit === "minutes" ? 60 : 1);
                const divisor = seconds % 86400 === 0 ? 86400 : seconds % 3600 === 0 ? 3600 : seconds % 60 === 0 ? 60 : 1;
                const amount = seconds / divisor;
                const unit = divisor === 86400 ? (this.french ? "jour" : "day") : divisor === 3600 ? (this.french ? "heure" : "hour") : divisor === 60 ? "minute" : (this.french ? "seconde" : "second");
                this.amountLabelTarget.textContent = String(amount);
                this.unitLabelTarget.textContent = unit + (amount === 1 ? "" : "s");
            }
            const planId = event.detail.xhr?.getResponseHeader("X-Action-Plan-Id");
            if (planId) {
                const values = JSON.parse(this.element.getAttribute("hx-vals") ?? "{}");
                this.element.setAttribute("hx-vals", JSON.stringify({ ...values, actionPlanId: planId }));
            }
            if (this.pending) { this.pending = false; queueMicrotask(() => this.save()); }
        } else { this.pending = false; this.errorTarget.classList.remove("hidden"); }
        if (!this.busy && this.closed) this.element.removeAttribute("data-stream-preserve-self");
    }

    private get fixed(): boolean { return this.element.dataset.waitMode === "fixed"; }
    private get closed(): boolean {
        return this.fixed ? this.controlsTarget.classList.contains("hidden")
            : this.amountTarget.classList.contains("hidden") && this.unitTarget.classList.contains("hidden");
    }
    private get french(): boolean { return this.element.dataset.waitLocale === "fr"; }
    private withSeconds(value: string): string { return value.length === 16 ? `${value}:00` : value; }
    private localDate(date: Date): string {
        const pad = (value: number) => String(value).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }
    private value(): string {
        return JSON.stringify({ mode: this.fixed ? "fixed" : "relative", amount: this.fixed ? "" : this.amountTarget.value, unit: this.fixed ? "" : this.unitTarget.value, date: this.fixed ? this.dateTarget.value : "" } satisfies Timing);
    }
}
